import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import {
  LifestyleQuestionnaireTypes,
  MentalHealthQuestionnaireTypes,
  type QuestionnaireResponseData,
} from "@/types/questionnaires";

export enum QuestionnaireCategory {
  LIFESTYLE = "lifestyle",
  MENTAL_HEALTH = "mental-health",
}

const QUESTIONNAIRE_CATEGORY_TO_TYPE_MAP = {
  [QuestionnaireCategory.LIFESTYLE]: Object.values(LifestyleQuestionnaireTypes),
  [QuestionnaireCategory.MENTAL_HEALTH]: Object.values(
    MentalHealthQuestionnaireTypes,
  ),
};

export const getQuestionnaireCategory = (
  questionnaireType:
    | LifestyleQuestionnaireTypes
    | MentalHealthQuestionnaireTypes,
): "lifestyle" | "mental-health" => {
  // Check if it's a lifestyle questionnaire
  if (
    QUESTIONNAIRE_CATEGORY_TO_TYPE_MAP[
      QuestionnaireCategory.LIFESTYLE
    ].includes(questionnaireType as LifestyleQuestionnaireTypes)
  ) {
    return "lifestyle";
  }
  // Otherwise it's mental health
  return "mental-health";
};

export interface GetQuestionnaireDataResponse {
  patientId: string;
  questionnaireResponses: Array<QuestionnaireResponseData>;
}

export const useGetQuestionnaire = (
  patientId: string | undefined,
  questionnairesCategory: QuestionnaireCategory,
) => {
  return useApi<GetQuestionnaireDataResponse, Array<QuestionnaireResponseData>>(
    {
      queryOptions: {
        enabled: Boolean(patientId),
        staleTime: 30000, // 30 seconds stale time here so we refetch when a new chart is loaded
        queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.DEFAULT(
          patientId,
          questionnairesCategory,
        ),
        queryFn: async () => {
          const questionnaireTypesParam = QUESTIONNAIRE_CATEGORY_TO_TYPE_MAP[
            questionnairesCategory
          ]
            .map((type) => `questionnaire_types=${type}`)
            .join("&");
          const response = await axiosInstance.get(
            `/charting/questionnaire/?${questionnaireTypesParam}`,
            {
              params: { patientId },
            },
          );
          return response?.data;
        },
        select: (data) => {
          const allQuestionnaires = data.questionnaireResponses;
          // Sort alphabetically by display name (aka short label)
          allQuestionnaires.sort((a, b) =>
            a.questionnaireShortLabel.localeCompare(b.questionnaireShortLabel),
          );
          return allQuestionnaires;
        },
      },
    },
  );
};
